// src/components/admin/CoreAdminPaymentManagement.js
import React, { useState, useEffect } from 'react';
import { licenseService } from '../../services/licenseService';
import { apiRequest } from '../../services/api';
import { usePageTitle } from '../../hooks/usePageTitle';
import SearchableSelect from '../common/SearchableSelect';
import './CoreAdminPaymentManagement.css';

const CoreAdminPaymentManagement = () => {
    usePageTitle('Payment Management');
    const [pendingPayments, setPendingPayments] = useState([]);
    const [confirmingPayments, setConfirmingPayments] = useState([]);
    const [rejectedPayments, setRejectedPayments] = useState([]);
    const [discountRequests, setDiscountRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('pending_payments');

    // Pay for Organization states
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [staffCount, setStaffCount] = useState(5);
    const [paymentNotes, setPaymentNotes] = useState('');

    // Pagination states
    const [pendingPage, setPendingPage] = useState(1);
    const [confirmingPage, setConfirmingPage] = useState(1);
    const [rejectedPage, setRejectedPage] = useState(1);
    const [discountPage, setDiscountPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [pendingTotal, setPendingTotal] = useState(0);
    const [confirmingTotal, setConfirmingTotal] = useState(0);
    const [rejectedTotal, setRejectedTotal] = useState(0);
    const [discountTotal, setDiscountTotal] = useState(0);
    const [discountPendingTotal, setDiscountPendingTotal] = useState(0);

    // Search states
    const [pendingSearch, setPendingSearch] = useState('');
    const [confirmingSearch, setConfirmingSearch] = useState('');
    const [rejectedSearch, setRejectedSearch] = useState('');
    const [discountSearch, setDiscountSearch] = useState('');

    // Load all counts on initial mount
    useEffect(() => {
        loadAllCounts();
    }, []);

    // Load data when tab changes or pagination/search changes
    useEffect(() => {
        loadData();
    }, [activeTab, pendingPage, confirmingPage, rejectedPage, discountPage, pendingSearch, confirmingSearch, rejectedSearch, discountSearch]);

    const loadAllCounts = async () => {
        // Load counts for all tabs to display badges
        try {
            // Load pending payments count
            const pendingResponse = await apiRequest.get('/payments/pending', {
                params: { limit: 0, offset: 0, status: 'pending' }
            });
            setPendingTotal(pendingResponse.data.total || 0);

            // Load confirming payments count
            const confirmingResponse = await apiRequest.get('/payments/pending', {
                params: { limit: 0, offset: 0, status: 'confirming' }
            });
            setConfirmingTotal(confirmingResponse.data.total || 0);

            // Load pending discount requests count for amber badge indicator
            const discountResponse = await apiRequest.get('/discounts/requests', {
                params: { limit: 0, offset: 0, status: 'pending' }
            });
            setDiscountPendingTotal(discountResponse.data.total || 0);
        } catch (error) {
            console.error('Failed to load counts:', error);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);

            if (activeTab === 'pending_payments') {
                await loadPendingPayments();
            } else if (activeTab === 'confirming_payments') {
                await loadConfirmingPayments();
            } else if (activeTab === 'rejected_payments') {
                await loadRejectedPayments();
            } else if (activeTab === 'discount_requests') {
                await loadDiscountRequests();
            } else if (activeTab === 'pay_for_org') {
                await loadOrganizations();
            }
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPendingPayments = async () => {
        try {
            const offset = (pendingPage - 1) * itemsPerPage;
            const response = await apiRequest.get('/payments/pending', {
                params: {
                    limit: itemsPerPage,
                    offset: offset,
                    search: pendingSearch,
                    status: 'pending'
                }
            });
            setPendingPayments(response.data.data || []);
            setPendingTotal(response.data.total || 0);
        } catch (error) {
            console.error('Failed to load pending payments:', error);
        }
    };

    const loadConfirmingPayments = async () => {
        try {
            const offset = (confirmingPage - 1) * itemsPerPage;
            const response = await apiRequest.get('/payments/pending', {
                params: {
                    limit: itemsPerPage,
                    offset: offset,
                    search: confirmingSearch,
                    status: 'confirming'
                }
            });
            setConfirmingPayments(response.data.data || []);
            setConfirmingTotal(response.data.total || 0);
        } catch (error) {
            console.error('Failed to load confirming payments:', error);
        }
    };

    const loadRejectedPayments = async () => {
        try {
            const offset = (rejectedPage - 1) * itemsPerPage;
            const response = await apiRequest.get('/payments/pending', {
                params: {
                    limit: itemsPerPage,
                    offset,
                    search: rejectedSearch,
                    status: 'failed'
                }
            });
            setRejectedPayments(response.data.data || []);
            setRejectedTotal(response.data.total || 0);
        } catch (error) {
            console.error('Failed to load rejected payments:', error);
        }
    };

    const loadDiscountRequests = async () => {
        try {
            const offset = (discountPage - 1) * itemsPerPage;
            const response = await apiRequest.get('/discounts/requests', {
                params: {
                    limit: itemsPerPage,
                    offset: offset,
                    search: discountSearch
                }
            });
            setDiscountRequests(response.data.data || []);
            setDiscountTotal(response.data.total || 0);
        } catch (error) {
            console.error('Failed to load discount requests:', error);
        }
    };

    const loadOrganizations = async () => {
        try {
            const response = await apiRequest.get('/organizations/list');
            const raw = response.data.data || [];
            const shaped = raw.map(org => {
                let licenseInfo;
                if (org.current_licenses) {
                    const expiry = org.subscription_end_date
                        ? new Date(org.subscription_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'No expiry';
                    licenseInfo = `${org.current_licenses} license${org.current_licenses !== 1 ? 's' : ''} · Exp: ${expiry}`;
                } else {
                    licenseInfo = 'No active subscription';
                }
                return {
                    ...org,
                    display_name: `${org.name} — ${licenseInfo}`
                };
            });
            setOrganizations(shaped);
        } catch (error) {
            console.error('Failed to load organizations:', error);
        }
    };

    const handleActivatePayment = async (paymentId) => {
        if (!window.confirm('Are you sure you want to activate this license? This action cannot be undone.')) {
            return;
        }

        try {
            setLoading(true);
            await licenseService.activateLicense(paymentId);
            alert('License activated successfully!');

            // Reload current tab data and refresh all counts
            if (activeTab === 'pending_payments') {
                await loadPendingPayments();
            } else if (activeTab === 'confirming_payments') {
                await loadConfirmingPayments();
            }
            await loadAllCounts();
        } catch (error) {
            console.error('Failed to activate license:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRejectPayment = async (paymentId, reason) => {
        const rejectionReason = prompt('Enter reason for rejection:', reason || '');

        if (!rejectionReason) {
            return;
        }

        try {
            setLoading(true);
            await apiRequest.post('/payments/reject', {
                payment_id: paymentId,
                reason: rejectionReason
            });
            alert('Payment rejected');

            // Reload source tab and refresh all counts
            if (activeTab === 'pending_payments') {
                await loadPendingPayments();
            } else if (activeTab === 'confirming_payments') {
                await loadConfirmingPayments();
            } else if (activeTab === 'rejected_payments') {
                await loadRejectedPayments();
            }
            await loadAllCounts();
        } catch (error) {
            console.error('Failed to reject payment:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleProcessDiscount = async (requestId, status) => {
        let approvedPercentage = null;

        if (status === 'approved') {
            const percentage = prompt('Enter approved discount percentage (1-50):');
            if (!percentage) return;

            approvedPercentage = parseFloat(percentage);
            if (approvedPercentage < 1 || approvedPercentage > 50) {
                alert('Discount must be between 1% and 50%');
                return;
            }
        }

        try {
            setLoading(true);
            await licenseService.processDiscountRequest(requestId, status, approvedPercentage);
            alert(`Discount request ${status}`);

            // Reload discount requests and refresh all counts
            await loadDiscountRequests();
            await loadAllCounts();
        } catch (error) {
            console.error('Failed to process discount:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePayForOrganization = async (e) => {
        e.preventDefault();

        if (!selectedOrg) {
            alert('Please select an organization');
            return;
        }

        if (staffCount < 1) {
            alert('Staff count must be at least 1');
            return;
        }

        if (!window.confirm(`Activate license for ${staffCount} staff? This will activate immediately without payment.`)) {
            return;
        }

        try {
            setLoading(true);
            await licenseService.adminPayForOrganization(selectedOrg, staffCount, paymentNotes);
            alert('License activated successfully for organization!');

            // Reset form
            setSelectedOrg('');
            setStaffCount(5);
            setPaymentNotes('');
        } catch (error) {
            console.error('Failed to process payment:', error);
            alert(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="core-admin-payment-management">
            <div className="page-header">
                <h2>Payment Management</h2>
                <p>Manage payments, discount requests, and activate licenses</p>
            </div>

            <div className="management-tabs">
                <button
                    className={`tab ${activeTab === 'pending_payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending_payments')}
                >
                    <i className="fas fa-clock"></i>
                    Pending Payments
                    {pendingTotal > 0 && (
                        <span className="badge">{pendingTotal}</span>
                    )}
                </button>
                <button
                    className={`tab ${activeTab === 'confirming_payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('confirming_payments')}
                >
                    <i className="fas fa-hourglass-half"></i>
                    Confirming Payments
                    {confirmingTotal > 0 && (
                        <span className="badge">{confirmingTotal}</span>
                    )}
                </button>
                <button
                    className={`tab ${activeTab === 'rejected_payments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rejected_payments')}
                >
                    <i className="fas fa-ban"></i>
                    Rejected Payments
                </button>
                <button
                    className={`tab ${activeTab === 'discount_requests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('discount_requests')}
                >
                    <i className="fas fa-tag"></i>
                    Discount Requests
                    {discountPendingTotal > 0 && (
                        <span className="badge">{discountPendingTotal}</span>
                    )}
                </button>
                <button
                    className={`tab ${activeTab === 'pay_for_org' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pay_for_org')}
                >
                    <i className="fas fa-hand-holding-usd"></i>
                    Pay for Organization
                </button>
            </div>

            <div className="tab-content">
                {/* Pending Payments Tab */}
                {activeTab === 'pending_payments' && (
                    <div className="pending-payments-section">
                        <div className="section-controls">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by organization, reference, or email..."
                                    value={pendingSearch}
                                    onChange={(e) => {
                                        setPendingSearch(e.target.value);
                                        setPendingPage(1); // Reset to first page on search
                                    }}
                                />
                                {pendingSearch && (
                                    <button
                                        className="clear-search"
                                        onClick={() => setPendingSearch('')}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading payments...</p>
                            </div>
                        ) : pendingPayments.length === 0 ? (
                            <div className="empty-state">
                                <i className="fas fa-check-circle"></i>
                                <h3>No Pending Payments</h3>
                                <p>{pendingSearch ? 'No payments match your search' : 'All payments have been processed'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="payments-table-container">
                                    <table className="payments-table">
                                        <thead>
                                            <tr>
                                                <th>Organization</th>
                                                <th>Reference</th>
                                                <th>Amount</th>
                                                <th>Staff Count</th>
                                                <th>Months</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {pendingPayments.map((payment) => (
                                            <tr key={payment.id}>
                                                <td>
                                                    <div className="org-cell">
                                                        <strong>{payment.organization_name}</strong>
                                                        <small>{payment.organization_email}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <code>{payment.reference}</code>
                                                </td>
                                                <td>
                                                    <strong>{payment.currency} {parseFloat(payment.amount).toFixed(2)}</strong>
                                                </td>
                                                <td>{payment.staff_count}</td>
                                                <td>{payment.duration_months || 1}</td>
                                                <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                                                <td>
                                                    <span className={`status-badge ${payment.status}`}>
                                                        {payment.status === 'confirming' ? 'User Confirmed' : payment.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            className="btn-activate"
                                                            onClick={() => handleActivatePayment(payment.id)}
                                                            disabled={loading}
                                                        >
                                                            <i className="fas fa-check"></i>
                                                            Activate
                                                        </button>
                                                        <button
                                                            className="btn-reject"
                                                            onClick={() => handleRejectPayment(payment.id)}
                                                            disabled={loading}
                                                        >
                                                            <i className="fas fa-times"></i>
                                                            Reject
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                            {pendingTotal > itemsPerPage && (
                                <div className="pagination">
                                    <button
                                        className="btn-page"
                                        onClick={() => setPendingPage(prev => Math.max(1, prev - 1))}
                                        disabled={pendingPage === 1}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                        Previous
                                    </button>
                                    <div className="pagination-info">
                                        <span>
                                            Page {pendingPage} of {Math.ceil(pendingTotal / itemsPerPage)}
                                        </span>
                                        <span className="total-records">
                                            ({pendingTotal} total)
                                        </span>
                                    </div>
                                    <button
                                        className="btn-page"
                                        onClick={() => setPendingPage(prev => Math.min(Math.ceil(pendingTotal / itemsPerPage), prev + 1))}
                                        disabled={pendingPage >= Math.ceil(pendingTotal / itemsPerPage)}
                                    >
                                        Next
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            )}
                        </>
                        )}
                    </div>
                )}

                {/* Confirming Payments Tab */}
                {activeTab === 'confirming_payments' && (
                    <div className="confirming-payments-section">
                        <div className="section-controls">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by organization, reference, or email..."
                                    value={confirmingSearch}
                                    onChange={(e) => {
                                        setConfirmingSearch(e.target.value);
                                        setConfirmingPage(1);
                                    }}
                                />
                                {confirmingSearch && (
                                    <button
                                        className="clear-search"
                                        onClick={() => setConfirmingSearch('')}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading confirming payments...</p>
                            </div>
                        ) : confirmingPayments.length === 0 ? (
                            <div className="empty-state">
                                <i className="fas fa-hourglass-half"></i>
                                <h3>No Confirming Payments</h3>
                                <p>{confirmingSearch ? 'No payments match your search' : 'No payments are awaiting confirmation'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="payments-table-container">
                                    <table className="payments-table">
                                        <thead>
                                            <tr>
                                                <th>Organization</th>
                                                <th>Reference</th>
                                                <th>Amount</th>
                                                <th>Staff Count</th>
                                                <th>Months</th>
                                                <th>Date</th>
                                                <th>Confirmed By User</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {confirmingPayments.map((payment) => (
                                                <tr key={payment.id} className="confirming-row">
                                                    <td>
                                                        <div className="org-cell">
                                                            <strong>{payment.organization_name}</strong>
                                                            <small>{payment.organization_email}</small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <code>{payment.reference}</code>
                                                    </td>
                                                    <td>
                                                        <strong>{payment.currency} {parseFloat(payment.amount).toFixed(2)}</strong>
                                                    </td>
                                                    <td>{payment.staff_count}</td>
                                                    <td>{payment.duration_months || 1}</td>
                                                    <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        <span className="status-badge confirming">
                                                            <i className="fas fa-check-circle"></i>
                                                            User Confirmed
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button
                                                                className="btn-activate"
                                                                onClick={() => handleActivatePayment(payment.id)}
                                                                disabled={loading}
                                                            >
                                                                <i className="fas fa-check"></i>
                                                                Activate
                                                            </button>
                                                            <button
                                                                className="btn-reject"
                                                                onClick={() => handleRejectPayment(payment.id)}
                                                                disabled={loading}
                                                            >
                                                                <i className="fas fa-times"></i>
                                                                Reject
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {confirmingTotal > itemsPerPage && (
                                    <div className="pagination">
                                        <button
                                            className="btn-page"
                                            onClick={() => setConfirmingPage(prev => Math.max(1, prev - 1))}
                                            disabled={confirmingPage === 1}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                            Previous
                                        </button>
                                        <div className="pagination-info">
                                            <span>
                                                Page {confirmingPage} of {Math.ceil(confirmingTotal / itemsPerPage)}
                                            </span>
                                            <span className="total-records">
                                                ({confirmingTotal} total)
                                            </span>
                                        </div>
                                        <button
                                            className="btn-page"
                                            onClick={() => setConfirmingPage(prev => Math.min(Math.ceil(confirmingTotal / itemsPerPage), prev + 1))}
                                            disabled={confirmingPage >= Math.ceil(confirmingTotal / itemsPerPage)}
                                        >
                                            Next
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Rejected Payments Tab */}
                {activeTab === 'rejected_payments' && (
                    <div className="rejected-payments-section">
                        <div className="section-controls">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by organisation, reference, or email..."
                                    value={rejectedSearch}
                                    onChange={(e) => {
                                        setRejectedSearch(e.target.value);
                                        setRejectedPage(1);
                                    }}
                                />
                                {rejectedSearch && (
                                    <button
                                        className="clear-search"
                                        onClick={() => setRejectedSearch('')}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading rejected payments...</p>
                            </div>
                        ) : rejectedPayments.length === 0 ? (
                            <div className="empty-state">
                                <i className="fas fa-ban"></i>
                                <h3>No Rejected Payments</h3>
                                <p>{rejectedSearch ? 'No payments match your search' : 'No payments have been rejected'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="payments-table-container">
                                    <table className="payments-table">
                                        <thead>
                                            <tr>
                                                <th>Organisation</th>
                                                <th>Reference</th>
                                                <th>Amount</th>
                                                <th>Staff Count</th>
                                                <th>Months</th>
                                                <th>Date</th>
                                                <th>Rejection Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rejectedPayments.map((payment) => {
                                                const notes = payment.payment_notes || '';
                                                const reasonMatch = notes.match(/Rejected by admin:\s*([^|]+)/);
                                                const reason = reasonMatch ? reasonMatch[1].trim() : 'No reason provided';
                                                return (
                                                    <tr key={payment.id} className="rejected-row">
                                                        <td>
                                                            <div className="org-cell">
                                                                <strong>{payment.organization_name}</strong>
                                                                <small>{payment.organization_email}</small>
                                                            </div>
                                                        </td>
                                                        <td><code>{payment.reference}</code></td>
                                                        <td><strong>{payment.currency} {parseFloat(payment.amount).toFixed(2)}</strong></td>
                                                        <td>{payment.staff_count}</td>
                                                        <td>{payment.duration_months || 1}</td>
                                                        <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                                                        <td className="rejection-reason-cell">{reason}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {rejectedTotal > itemsPerPage && (
                                    <div className="pagination">
                                        <button
                                            className="btn-page"
                                            onClick={() => setRejectedPage(prev => Math.max(1, prev - 1))}
                                            disabled={rejectedPage === 1}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                            Previous
                                        </button>
                                        <div className="pagination-info">
                                            <span>Page {rejectedPage} of {Math.ceil(rejectedTotal / itemsPerPage)}</span>
                                            <span className="total-records">({rejectedTotal} total)</span>
                                        </div>
                                        <button
                                            className="btn-page"
                                            onClick={() => setRejectedPage(prev => Math.min(Math.ceil(rejectedTotal / itemsPerPage), prev + 1))}
                                            disabled={rejectedPage >= Math.ceil(rejectedTotal / itemsPerPage)}
                                        >
                                            Next
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Discount Requests Tab */}
                {activeTab === 'discount_requests' && (
                    <div className="discount-requests-section">
                        <div className="section-controls">
                            <div className="search-box">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Search by organization or email..."
                                    value={discountSearch}
                                    onChange={(e) => {
                                        setDiscountSearch(e.target.value);
                                        setDiscountPage(1);
                                    }}
                                />
                                {discountSearch && (
                                    <button
                                        className="clear-search"
                                        onClick={() => setDiscountSearch('')}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading discount requests...</p>
                            </div>
                        ) : discountRequests.length === 0 ? (
                            <div className="empty-state">
                                <i className="fas fa-tag"></i>
                                <h3>No Discount Requests</h3>
                                <p>{discountSearch ? 'No discount requests match your search' : 'No organizations have requested discounts'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="discount-requests-list">
                                {discountRequests.map((request) => (
                                    <div key={request.id} className={`discount-request-card ${request.status}`}>
                                        <div className="request-header">
                                            <div className="request-org">
                                                <h4>{request.organization_name}</h4>
                                                <p>{request.organization_email}</p>
                                            </div>
                                            <div className="request-percentage">
                                                {request.status === 'approved'
                                                    ? request.approved_percentage
                                                    : request.requested_percentage}%
                                            </div>
                                        </div>

                                        <div className="request-body">
                                            <div className="request-reason">
                                                <strong>Reason:</strong>
                                                <p>{request.reason}</p>
                                            </div>

                                            <div className="request-meta">
                                                <div className="meta-item">
                                                    <i className="fas fa-calendar"></i>
                                                    <span>Requested: {new Date(request.created_at).toLocaleDateString()}</span>
                                                </div>
                                                <div className="meta-item">
                                                    <i className="fas fa-user"></i>
                                                    <span>By: {request.requested_by_name}</span>
                                                </div>
                                                {request.invoice_reference && (
                                                    <div className="meta-item">
                                                        <i className="fas fa-file-invoice"></i>
                                                        <span>Invoice: {request.invoice_reference}</span>
                                                    </div>
                                                )}
                                                {request.invoice_staff_count && (
                                                    <div className="meta-item">
                                                        <i className="fas fa-users"></i>
                                                        <span>
                                                            {request.invoice_staff_count} staff / {request.invoice_duration_months || 1} month(s)
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="request-footer">
                                            <span className={`status-badge ${request.status}`}>
                                                {request.status}
                                            </span>

                                            {request.status === 'pending' && (
                                                <div className="request-actions">
                                                    <button
                                                        className="btn-approve"
                                                        onClick={() => handleProcessDiscount(request.id, 'approved')}
                                                        disabled={loading}
                                                    >
                                                        <i className="fas fa-check"></i>
                                                        Approve
                                                    </button>
                                                    <button
                                                        className="btn-reject"
                                                        onClick={() => handleProcessDiscount(request.id, 'rejected')}
                                                        disabled={loading}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                        Reject
                                                    </button>
                                                </div>
                                            )}

                                            {request.status === 'approved' && (
                                                <div className="approved-info">
                                                    <i className="fas fa-check-circle"></i>
                                                    Approved: {request.approved_percentage}%
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                </div>

                                {/* Pagination */}
                                {discountTotal > itemsPerPage && (
                                    <div className="pagination">
                                        <button
                                            className="btn-page"
                                            onClick={() => setDiscountPage(prev => Math.max(1, prev - 1))}
                                            disabled={discountPage === 1}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                            Previous
                                        </button>
                                        <div className="pagination-info">
                                            <span>
                                                Page {discountPage} of {Math.ceil(discountTotal / itemsPerPage)}
                                            </span>
                                            <span className="total-records">
                                                ({discountTotal} total)
                                            </span>
                                        </div>
                                        <button
                                            className="btn-page"
                                            onClick={() => setDiscountPage(prev => Math.min(Math.ceil(discountTotal / itemsPerPage), prev + 1))}
                                            disabled={discountPage >= Math.ceil(discountTotal / itemsPerPage)}
                                        >
                                            Next
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Pay for Organization Tab */}
                {activeTab === 'pay_for_org' && (
                    <div className="pay-for-org-section">
                        <div className="section-info">
                            <i className="fas fa-info-circle"></i>
                            <p>
                                Activate licenses for organizations without requiring payment.
                                This is useful for promotional offers, partnerships, or special cases.
                            </p>
                        </div>

                        <form onSubmit={handlePayForOrganization} className="pay-form">
                            <div className="form-group">
                                <label htmlFor="organization">
                                    Select Organization <span className="required">*</span>
                                </label>
                                <SearchableSelect
                                    value={selectedOrg ? parseInt(selectedOrg) : null}
                                    onChange={(val) => setSelectedOrg(val ? String(val) : '')}
                                    options={organizations}
                                    valueKey="id"
                                    displayKey="display_name"
                                    placeholder="-- Select Organisation --"
                                    searchPlaceholder="Search by name..."
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="staff_count">
                                    Number of Staff Licenses <span className="required">*</span>
                                </label>
                                <input
                                    type="number"
                                    id="staff_count"
                                    min="1"
                                    value={staffCount}
                                    onChange={(e) => setStaffCount(parseInt(e.target.value) || 1)}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="notes">
                                    Payment Notes (Optional)
                                </label>
                                <textarea
                                    id="notes"
                                    rows="4"
                                    value={paymentNotes}
                                    onChange={(e) => setPaymentNotes(e.target.value)}
                                    placeholder="Add any notes about this payment (e.g., 'Promotional offer', 'Partnership agreement', etc.)"
                                />
                            </div>

                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-small"></span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-bolt"></i>
                                        Activate License Immediately
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CoreAdminPaymentManagement;
